import { useState, useEffect, useCallback, useRef } from "react";
import { Box } from "@mui/material";
import { useParams, useLocation, useNavigate } from "react-router-dom";
import { useGetPostsQuery } from "@/store/api/postApi";
import { GetPostsParams, IPost } from "@/store/api/postApiData";
import StockCard from "./StockCard";
import Filter from "../Home/components/Filter";
import Post from "../Home/components/Post";
import { useGetCompanyInfoQuery } from "@/store/api/financeApi";
import CustomLoader from "@/components/UI/CustomLoader/CustomLoader";
import { useAppSelector } from "@/hooks/useAppSelector";
import { allTimeOptions, PREMIUM_TIME_OPTIONS } from "@/types/timeRanges";
import { formatMarketCap } from "./formatMarketCap";
import { RootState } from "@/store/store";
import { useSelector } from "react-redux";

const POSTS_LIMIT = 50;

const Stock = () => {
  const { symbol } = useParams<{ symbol: string }>();
  const location = useLocation();
  const [page, setPage] = useState(1);
  const [allPosts, setAllPosts] = useState<IPost[]>([]);
  const [hasMore, setHasMore] = useState(true);
  const observer = useRef<IntersectionObserver | null>(null);
  const loadMoreRef = useRef<HTMLDivElement | null>(null);
  const { subscriptionStatus } = useAppSelector((state) => state.auth);
  const hasPremium = subscriptionStatus === "ACTIVE";

  const [params, setParams] = useState<GetPostsParams>({
    limit: POSTS_LIMIT,
    text: "",
    author: "",
    cashtag: symbol,
    hashtag: "",
    timeRange: "",
    who: "",
    order: "desc",
    subtype: "",
    topic: "",
  });

  const [sortDirection, setSortDirection] = useState<"asc" | "desc">("desc");
  const [refreshCount, setRefreshCount] = useState(0);
  const paramsQuery = new URLSearchParams(location.search);
  const urlTimeValue = paramsQuery.get("timeRange") || "";
  const urlWhoValue = paramsQuery.get("who") || "";
  const navigate = useNavigate();
  const { postLoading, editingPost, draftLoading } = useSelector(
    (state: RootState) => state.loading
  );

  const foundTime = allTimeOptions.find((t) => t.value === urlTimeValue);
  let initialTimeRange: string | undefined;
  if (foundTime) {
    const isPremiumOption = PREMIUM_TIME_OPTIONS.some((opt) => opt.value === foundTime.value);
    if (!isPremiumOption || hasPremium) {
      initialTimeRange = foundTime.label;
    }
  }

  const initialWho = (() => {
    if (!urlWhoValue) return undefined;
    const lower = urlWhoValue.toLowerCase();
    if (lower === "all") return "All";
    if (lower === "following") return "Following";
    return urlWhoValue;
  })();
  const initialCategory = (() => {
    const subtype = paramsQuery.get("subtype")?.toLowerCase();
    if (!subtype) return "";
    if (subtype === "technical") return "Stocks - Technical";
    if (subtype === "fundamental") return "Stocks - Fundamental";
    return "";
  })();

  const updateUrlParams = (newParams: Record<string, string>) => {
    const paramsQuery = new URLSearchParams(location.search);

    Object.entries(newParams).forEach(([key, value]) => {
      if (value) paramsQuery.set(key, value);
      else paramsQuery.delete(key);
    });

    navigate({ search: paramsQuery.toString() }, { replace: true });
  };

  const {
    data: company,
    isLoading: companyLoading,
    isFetching: companyFetching,
    error: companyError,
  } = useGetCompanyInfoQuery(symbol || "", {
    refetchOnMountOrArgChange: true,
  });

  const {
    data: postsResponse,
    error,
    isLoading,
    isFetching,
    refetch,
  } = useGetPostsQuery({ ...params, cashtag: symbol, page }, { refetchOnMountOrArgChange: true });

  useEffect(() => {
    const paramsQuery = new URLSearchParams(location.search);
    const timeRangeFromUrl = paramsQuery.get("timeRange") || "";
    const whoFromUrl = paramsQuery.get("who");
    const subtypeFromUrl = paramsQuery.get("subtype") || "";
    setParams((prev) => ({
      ...prev,
      timeRange: timeRangeFromUrl,
      who: whoFromUrl ? whoFromUrl.toLowerCase() : prev.who,
      subtype: subtypeFromUrl,
    }));
  }, [location.search]);

  useEffect(() => {
    if (!postsResponse?.data) return;

    const postsArray = postsResponse.data;

    setAllPosts((prev) => {
      if (page === 1) {
        return postsArray;
      }

      const newPosts = postsArray.filter((p) => !prev.some((ap) => ap.uid === p.uid));

      return [...prev, ...newPosts];
    });

    setHasMore(postsArray.length === POSTS_LIMIT);
  }, [postsResponse, page, refreshCount]);

  const isFetchingRef = useRef(isFetching);
  const hasMoreRef = useRef(hasMore);
  useEffect(() => {
    isFetchingRef.current = isFetching;
  }, [isFetching]);
  useEffect(() => {
    hasMoreRef.current = hasMore;
  }, [hasMore]);

  const loadNextPage = useCallback(() => {
    if (!isFetchingRef.current && hasMoreRef.current) {
      setPage((prev) => prev + 1);
    }
  }, []);

  useEffect(() => {
    // the sentinel only mounts once the early "company loading" return is
    // past, so the effect must re-run when that flips to (re)attach
    if (companyLoading || companyFetching || !loadMoreRef.current) return;

    observer.current = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) {
          loadNextPage();
        }
      },
      { threshold: 0.1 }
    );

    observer.current.observe(loadMoreRef.current);

    return () => observer.current?.disconnect();
  }, [loadNextPage, companyLoading, companyFetching]);

  useEffect(() => {
    setAllPosts([]);
    setPage(1);
    setHasMore(true);
  }, [
    symbol,
    params.text,
    params.author,
    params.hashtag,
    params.timeRange,
    params.who,
    params.order,
    params.subtype,
    params.topic,
  ]);

  useEffect(() => {
    const paramsQuery = new URLSearchParams(location.search);
    const postId = paramsQuery.get("post");
    if (postId && allPosts.length > 0) {
      const timeoutId = setTimeout(() => {
        const el = document.getElementById(`post-${postId}`);
        if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
      }, 0);
      return () => clearTimeout(timeoutId);
    }
  }, [location.search, allPosts]);

  const onRefreshPosts = () => {
    setPage(1);
    setAllPosts([]);
    setRefreshCount((prev) => prev + 1);
    refetch();
  };

  if (companyLoading || companyFetching) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", height: "100%" }}>
        <CustomLoader />
      </Box>
    );
  }

  if (companyError || !company) return <div>Error loading company</div>;
  const handleDeletePost = (uid: string) => {
    setAllPosts((prev) => prev.filter((post) => post.uid !== uid));
  };
  const formattedSymbol = symbol ? symbol.replace(/^\$/, "").toUpperCase() : "";

  return (
    <Box
      className="hide-scrollbar"
      sx={{
        height: "100%",
        display: "flex",
        flexDirection: "column",
        overflowY: "auto",
      }}
    >
      <StockCard
        company={company.companyName}
        symbol={formattedSymbol}
        market={company.exchange}
        price={company.price}
        change={company.change}
        changePercent={company.changePercent}
        marketTime={company.updatedAt!}
        isFocused={company.isFocused}
        industry={company.industry}
        sector={company.sector}
        marketCap={formatMarketCap(company.marketCap)}
        forwardPE={company.forwardPE?.toFixed(2)}
        targetPrice={company.wallStreetTargetPrice?.toFixed(2)}
        description={company.description}
        website={company.webURL}
      />

      <Box sx={{ position: "sticky", top: 0 }}>
        <Filter
          setParams={setParams}
          sortDirection={sortDirection}
          setSortDirection={setSortDirection}
          onRefreshPosts={onRefreshPosts}
          limit={POSTS_LIMIT}
          isPremium={hasPremium}
          initialTimeRange={initialTimeRange}
          initialWho={initialWho}
          updateUrlParams={updateUrlParams}
          initialCategory={initialCategory}
        />
      </Box>
      {!editingPost.loading &&
        (isFetching || postLoading || draftLoading) &&
        !isLoading &&
        allPosts.length > 0 && (
          <Box sx={{ display: "flex", justifyContent: "center", py: 2 }}>
            <CustomLoader size={34} />
          </Box>
        )}
      <Box sx={{ flex: 1, display: "flex", flexDirection: "column", gap: "0.75rem" }}>
        {allPosts.map((post) => {
          const isEditingThisPost = editingPost.loading && editingPost.postId === post.uid;

          return (
            <Post
              post={post}
              key={post.uid}
              onDelete={handleDeletePost}
              isEditing={isEditingThisPost}
            />
          );
        })}

        <div ref={loadMoreRef} style={{ height: "80px" }} />

        {!editingPost.loading && (isLoading || isFetching) && (
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              padding: 2,
              mt: "-80px",
            }}
          >
            <CustomLoader />
          </Box>
        )}

        {error && <div>Error loading posts</div>}

        {!hasMore && allPosts.length > 0 && (
          <Box sx={{ textAlign: "center", padding: 2, color: "text.secondary", mt: "-80px" }}>
            No more posts
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default Stock;
